/**
 * How a query matches text, extracted verbatim from A2
 * (`apps/spa-prototype/src/variants/a2/search/search-index.ts:30-123`).
 *
 * Matching is accent- and case-insensitive (NFD, diacritics stripped), by
 * token: every meaningful word of the query must hit some field of an item.
 * Short tokens (≤ 2 letters) must hit the start of a word, so "b" finds
 * "Treino B" without lighting up every b in the catalogue.
 *
 * ── NOT FUZZY, ON PURPOSE ──────────────────────────────────────────────────
 * No typo tolerance and no edit distance: A2's search is not fuzzy, and this
 * module is parity first. What A2 searched over (`settingFields`, `actions`,
 * `MUSCLES`, `search()`) read the prototype's mock data and stayed behind:
 * the screen builds its own item list from real data and calls `rank`,
 * `score` and `highlight`.
 *
 * Pure: no React, no DOM, provable in plain Node.
 */
export const norm = (s) => s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
/** A2's stopwords, verbatim. An en-US screen passes its own list to `tokenize`. */
export const PT_STOPWORDS = new Set([
    "a",
    "o",
    "as",
    "os",
    "e",
    "de",
    "da",
    "do",
    "das",
    "dos",
    "em",
    "no",
    "na",
    "nos",
    "nas",
    "um",
    "uma",
    "pra",
    "para",
    "por",
    "com",
    "meu",
    "minha",
    "que",
    "como",
    "qual",
    "quais",
    "eu",
    "me",
    "ao",
    "se",
    "sobre",
]);
const TOKEN_SEPARATOR = /[^\p{L}\p{N}]+/u;
const WORD_CHAR = /[\p{L}\p{N}]/u;
/** The query's meaningful tokens, normalized — or all of them, if every one is a stopword. */
export function tokenize(query, stopwords = PT_STOPWORDS) {
    const all = norm(query).split(TOKEN_SEPARATOR).filter(Boolean);
    const meaningful = all.filter((t) => !stopwords.has(t));
    return meaningful.length > 0 ? meaningful : all;
}
const isLetter = (ch) => ch !== undefined && WORD_CHAR.test(ch);
/** Where `token` occurs in the normalized `hay`, honouring the word-start rule. */
function occurrences(hay, token) {
    const at = [];
    let from = 0;
    for (;;) {
        const i = hay.indexOf(token, from);
        if (i < 0) {
            return at;
        }
        if (token.length > 2 || !isLetter(hay[i - 1])) {
            at.push(i);
        }
        from = i + 1;
    }
}
/** The label split into runs, `hit` where a query token matched. Accents survive. */
export function highlight(text, tokens) {
    if (tokens.length === 0) {
        return [{ text, hit: false }];
    }
    let hay = "";
    const map = [];
    for (let i = 0; i < text.length; i += 1) {
        for (const ch of norm(text.charAt(i))) {
            hay += ch;
            map.push(i);
        }
    }
    const hit = new Array(text.length).fill(false);
    for (const token of tokens) {
        for (const start of occurrences(hay, token)) {
            for (let k = start; k < start + token.length; k += 1) {
                const original = map[k];
                if (original !== undefined) {
                    hit[original] = true;
                }
            }
        }
    }
    const runs = [];
    for (let i = 0; i < text.length; i += 1) {
        const last = runs.at(-1);
        if (last && last.hit === hit[i]) {
            last.text += text.charAt(i);
        }
        else {
            runs.push({ text: text.charAt(i), hit: hit[i] ?? false });
        }
    }
    return runs;
}
/** Sum over tokens of the best field weight that token hits; null if any token misses. */
export function score(fields, tokens) {
    let total = 0;
    for (const token of tokens) {
        let best = 0;
        for (const [text, weight] of fields) {
            const hay = norm(text);
            const at = occurrences(hay, token);
            if (at.length > 0) {
                const wordStart = at.some((i) => !isLetter(hay[i - 1]));
                best = Math.max(best, weight * (wordStart ? 1.25 : 1));
            }
        }
        if (best === 0) {
            return null;
        }
        total += best;
    }
    return total;
}
/**
 * The items every token of `query` hits, best first. Equal scores keep the
 * order they came in. A query with no token (empty, or only punctuation)
 * ranks nothing rather than everything — the screen shows its idle state.
 */
export function rank(items, fields, query, stopwords = PT_STOPWORDS) {
    const tokens = tokenize(query, stopwords);
    if (tokens.length === 0) {
        return [];
    }
    const hits = [];
    for (const item of items) {
        const s = score(fields(item), tokens);
        if (s !== null) {
            hits.push({ item, score: s });
        }
    }
    // `Array.prototype.sort` is stable, so ties keep their input order.
    return hits.sort((a, b) => b.score - a.score);
}
//# sourceMappingURL=search-index.js.map